import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { GridApi } from 'ag-grid-community'
import store, { AllState } from '../../../../store'
import { featureFlagService } from '../../../../utils/feature-flag'
import {
  getWbsItemDependencies,
  saveWbsItemDependencies,
  WbsItemDependencyResponse,
} from '../../../../infrastructure/api/wbs-item-dependencies'
import { successDummyResponse, APIResponse } from '../../../../lib/commons/api'
import { Dependency } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/GanttDependency/DependencyContext'
import { requireSave } from '../../../../store/requiredSaveData'
import { formatDateTime } from '../../../../utils/date'
import { WbsItemLinkSummary } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/WbsItemLink/WbsItemLinkDialog'
import { WbsItemInfo } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/types'
import { ProjectPlanNewRow } from '../projectPlanNew'

const getNameFromGrid = (api: GridApi, rowIndex: number): string => {
  const rowNode = api.getDisplayedRowAtIndex(rowIndex)
  if (!rowNode) return ''
  return (
    (rowNode.data as ProjectPlanNewRow | undefined)?.body?.wbsItem
      ?.displayName ?? ''
  )
}

const getIconUrlFromGrid = (
  api: GridApi,
  rowIndex: number
): string | undefined => {
  const rowNode = api.getDisplayedRowAtIndex(rowIndex)
  if (!rowNode) return undefined
  return (rowNode.data as ProjectPlanNewRow | undefined)?.body?.wbsItem
    ?.wbsItemType?.iconUrl
}

export const useWbsItemDependencyData = () => {
  const projectUuid = useSelector((state: AllState) => state.project.selected)
  const isEnabled = featureFlagService.isEnabled(
    'gantt-dependency-critical-path'
  )

  const [dependencies, setDependencies] = useState<Dependency[]>([])
  const [dialogItems, setDialogItems] = useState<WbsItemLinkSummary[]>([])
  const serverDataRef = useRef<WbsItemDependencyResponse[]>([])
  const currentDepsRef = useRef<Dependency[]>([])
  const dialogItemsRef = useRef<WbsItemLinkSummary[]>([])
  const gridApiRef = useRef<GridApi | null>(null)

  const setGridApi = useCallback((api: GridApi) => {
    gridApiRef.current = api
  }, [])

  const getGridApi = useCallback(() => gridApiRef.current, [])

  // Actual implementation - recreated when deps change
  const fetchDependenciesImpl = useCallback(
    async (uuidToRowIndex: ReadonlyMap<string, number>) => {
      if (!projectUuid || !isEnabled) return
      const response = await getWbsItemDependencies()
      if (response.hasError) return
      const serverData: WbsItemDependencyResponse[] = response.json ?? []
      serverDataRef.current = serverData

      // Include all links, using rowIndex: -1 for non-visible endpoints
      // (e.g. items in collapsed groups). categorizeLinks in GanttBackground
      // will separate visible vs fallback links for rendering.
      const deps: Dependency[] = serverData.map(s => ({
        uuid: s.uuid,
        fromRowIndex: uuidToRowIndex.get(s.fromWbsItemUuid) ?? -1,
        toRowIndex: uuidToRowIndex.get(s.toWbsItemUuid) ?? -1,
        fromWbsItemUuid: s.fromWbsItemUuid,
        toWbsItemUuid: s.toWbsItemUuid,
        fromProjectPlanUuid: s.fromProjectPlanUuid,
        toProjectPlanUuid: s.toProjectPlanUuid,
        fromEndDate: s.fromDate ? new Date(s.fromDate) : null,
        toStartDate: s.toDate ? new Date(s.toDate) : null,
        ...(s.color != null && { color: s.color }),
        ...(s.description != null && { description: s.description }),
        createdAt: formatDateTime(s.createdAt),
        createdBy: s.createdBy,
        updatedAt: formatDateTime(s.updatedAt),
        updatedBy: s.updatedBy,
      }))

      setDependencies(deps)
      currentDepsRef.current = deps

      // Populate dialog items from ALL server data (with names from API)
      const allDialogItems: WbsItemLinkSummary[] = serverData.map(s => ({
        uuid: s.uuid,
        fromWbsItemUuid: s.fromWbsItemUuid,
        toWbsItemUuid: s.toWbsItemUuid,
        fromName: s.fromWbsItem.displayName,
        toName: s.toWbsItem.displayName,
        fromIconUrl: s.fromWbsItem.typeDto?.iconUrl,
        toIconUrl: s.toWbsItem.typeDto?.iconUrl,
        color: s.color,
        description: s.description,
        createdAt: formatDateTime(s.createdAt),
        createdBy: s.createdBy,
        updatedAt: formatDateTime(s.updatedAt),
        updatedBy: s.updatedBy,
      }))
      dialogItemsRef.current = allDialogItems
      setDialogItems(allDialogItems)
    },
    [projectUuid, isEnabled]
  )

  // Stable ref that always points to the latest implementation
  const fetchImplRef = useRef(fetchDependenciesImpl)
  fetchImplRef.current = fetchDependenciesImpl

  // Stable function reference - never stale in closures
  const fetchDependencies = useCallback(
    (uuidToRowIndex: ReadonlyMap<string, number>) =>
      fetchImplRef.current(uuidToRowIndex),
    []
  )

  const onDependenciesChange = useCallback((deps: Dependency[]) => {
    const prevDeps = currentDepsRef.current
    currentDepsRef.current = deps

    // Sync changes to dialogItems
    setDialogItems(prev => {
      let updated = prev

      // Handle items removed via gantt
      const removedUuids = new Set(
        prevDeps
          .filter(p => !deps.some(d => d.uuid === p.uuid))
          .map(p => p.uuid)
      )
      if (removedUuids.size > 0) {
        updated = updated.filter(item => !removedUuids.has(item.uuid))
      }

      // Handle items added via gantt
      const newItems = deps.filter(d => !prev.some(p => p.uuid === d.uuid))
      if (newItems.length > 0) {
        const api = gridApiRef.current
        const newDialogItems: WbsItemLinkSummary[] = newItems.map(d => ({
          uuid: d.uuid,
          fromWbsItemUuid: d.fromWbsItemUuid,
          toWbsItemUuid: d.toWbsItemUuid,
          fromName: api
            ? getNameFromGrid(api, d.fromRowIndex)
            : d.fromWbsItemUuid,
          toName: api ? getNameFromGrid(api, d.toRowIndex) : d.toWbsItemUuid,
          fromIconUrl: api
            ? getIconUrlFromGrid(api, d.fromRowIndex)
            : undefined,
          toIconUrl: api ? getIconUrlFromGrid(api, d.toRowIndex) : undefined,
          color: d.color ?? null,
          description: d.description ?? null,
        }))
        updated = [...updated, ...newDialogItems]
      }

      // Sync color/description changes
      const depsMap = new Map(deps.map(d => [d.uuid, d]))
      updated = updated.map(item => {
        const dep = depsMap.get(item.uuid)
        if (!dep) return item
        const newColor = dep.color ?? null
        const newDesc = dep.description ?? null
        if (newColor === item.color && newDesc === item.description) {
          return item
        }
        return { ...item, color: newColor, description: newDesc }
      })

      dialogItemsRef.current = updated
      return updated
    })

    // requireSave check
    // All links (including non-visible with rowIndex: -1) are now in Context,
    // so no need to exclude non-visible items from deletion detection.
    const server = serverDataRef.current
    const hasAddedOrDeleted =
      deps.some(d => !server.some(s => s.uuid === d.uuid)) ||
      server.some(s => !deps.some(d => d.uuid === s.uuid))
    const hasPropChanges = deps.some(d => {
      const s = server.find(s => s.uuid === d.uuid)
      if (!s) return false
      return (
        (d.color ?? null) !== (s.color ?? null) ||
        (d.description ?? null) !== (s.description ?? null)
      )
    })
    if (hasAddedOrDeleted || hasPropChanges) {
      store.dispatch(requireSave())
    }
  }, [])

  const removeByWbsItemUuids = useCallback((uuids: Set<string>) => {
    const filtered = currentDepsRef.current.filter(
      d => !uuids.has(d.fromWbsItemUuid) && !uuids.has(d.toWbsItemUuid)
    )
    if (filtered.length !== currentDepsRef.current.length) {
      currentDepsRef.current = filtered
      setDependencies(filtered)

      // Also remove from dialogItems
      setDialogItems(prev => {
        const next = prev.filter(
          item =>
            !uuids.has(item.fromWbsItemUuid) && !uuids.has(item.toWbsItemUuid)
        )
        dialogItemsRef.current = next
        return next
      })

      store.dispatch(requireSave())
    }
  }, [])

  const updateLinkFromDialog = useCallback(
    (
      uuid: string,
      updates: {
        color?: string
        description?: string
        fromDate?: string
        toDate?: string
      }
    ) => {
      // Update dialogItems
      setDialogItems(prev => {
        const next = prev.map(item =>
          item.uuid === uuid
            ? {
                ...item,
                ...(updates.color !== undefined && {
                  color: updates.color ?? null,
                }),
                ...(updates.description !== undefined && {
                  description: updates.description ?? null,
                }),
              }
            : item
        )
        dialogItemsRef.current = next
        return next
      })

      // Update currentDepsRef and dependencies state (for visible items)
      currentDepsRef.current = currentDepsRef.current.map(d =>
        d.uuid === uuid ? { ...d, ...updates } : d
      )
      setDependencies(prev =>
        prev.map(d => (d.uuid === uuid ? { ...d, ...updates } : d))
      )

      store.dispatch(requireSave())
    },
    []
  )

  const removeLinkFromDialog = useCallback((uuid: string) => {
    // Update dialogItems
    setDialogItems(prev => {
      const next = prev.filter(item => item.uuid !== uuid)
      dialogItemsRef.current = next
      return next
    })

    // Update currentDepsRef and dependencies state
    currentDepsRef.current = currentDepsRef.current.filter(d => d.uuid !== uuid)
    setDependencies(prev => prev.filter(d => d.uuid !== uuid))

    store.dispatch(requireSave())
  }, [])

  const saveDependencies = useCallback(async (): Promise<APIResponse> => {
    if (!isEnabled) return successDummyResponse

    const current = currentDepsRef.current
    const allDialogCurrent = dialogItemsRef.current
    const server = serverDataRef.current

    const added = current
      .filter(d => !server.some(s => s.uuid === d.uuid))
      .map(d => ({
        uuid: d.uuid,
        fromWbsItemUuid: d.fromWbsItemUuid,
        toWbsItemUuid: d.toWbsItemUuid,
        color: d.color,
        description: d.description,
      }))

    // Visible items that were edited
    const editedFromContext = current
      .filter(d => {
        const s = server.find(s => s.uuid === d.uuid)
        if (!s) return false
        return (
          (d.color ?? null) !== (s.color ?? null) ||
          (d.description ?? null) !== (s.description ?? null)
        )
      })
      .map(d => ({
        uuid: d.uuid,
        color: d.color,
        description: d.description,
      }))

    // Non-visible items that were edited via dialog
    const editedFromDialog = allDialogCurrent
      .filter(d => {
        if (current.some(c => c.uuid === d.uuid)) return false
        const s = server.find(s => s.uuid === d.uuid)
        if (!s) return false
        return (
          (d.color ?? null) !== (s.color ?? null) ||
          (d.description ?? null) !== (s.description ?? null)
        )
      })
      .map(d => ({
        uuid: d.uuid,
        color: d.color ?? undefined,
        description: d.description ?? undefined,
      }))

    const edited = [...editedFromContext, ...editedFromDialog]

    // Use both currentDeps and dialogItems as authoritative sources for what should exist
    const allCurrentUuids = new Set([
      ...current.map(d => d.uuid),
      ...allDialogCurrent.map(d => d.uuid),
    ])
    const deleted = server
      .filter(s => !allCurrentUuids.has(s.uuid))
      .map(s => ({ uuid: s.uuid }))

    if (!added.length && !edited.length && !deleted.length) {
      return successDummyResponse
    }

    const response = await saveWbsItemDependencies({ added, edited, deleted })
    if (!response.hasError) {
      // Rebuild serverDataRef from all current items
      const newServer: WbsItemDependencyResponse[] = []

      // Visible items from currentDepsRef
      for (const d of current) {
        const existing = server.find(s => s.uuid === d.uuid)
        newServer.push({
          uuid: d.uuid,
          projectUuid: existing?.projectUuid ?? '',
          fromWbsItemUuid: d.fromWbsItemUuid,
          fromProjectPlanUuid:
            d.fromProjectPlanUuid ?? existing?.fromProjectPlanUuid ?? '',
          fromDate: existing?.fromDate ?? '',
          toWbsItemUuid: d.toWbsItemUuid,
          toProjectPlanUuid:
            d.toProjectPlanUuid ?? existing?.toProjectPlanUuid ?? '',
          toDate: existing?.toDate ?? '',
          color: d.color ?? null,
          description: d.description ?? null,
          lockVersion: existing?.lockVersion ?? 0,
          fromWbsItem: existing?.fromWbsItem ?? ({} as never),
          toWbsItem: existing?.toWbsItem ?? ({} as never),
          createdAt: existing?.createdAt ?? 0,
          createdBy: existing?.createdBy ?? ({} as never),
          updatedAt: existing?.updatedAt ?? 0,
          updatedBy: existing?.updatedBy ?? ({} as never),
        })
      }

      // Non-visible items still in dialogItems
      for (const d of allDialogCurrent) {
        if (current.some(c => c.uuid === d.uuid)) continue
        const existing = server.find(s => s.uuid === d.uuid)
        if (!existing) continue
        newServer.push({
          ...existing,
          color: d.color,
          description: d.description,
        })
      }

      serverDataRef.current = newServer
    }
    return response
  }, [isEnabled])

  // Build wbsItemUuid → info map from dialogItems (server-sourced display names/icons)
  const wbsItemInfoMap = useMemo(() => {
    const map = new Map<string, WbsItemInfo>()
    for (const item of dialogItems) {
      map.set(item.fromWbsItemUuid, {
        displayName: item.fromName,
        iconUrl: item.fromIconUrl ?? undefined,
      })
      map.set(item.toWbsItemUuid, {
        displayName: item.toName,
        iconUrl: item.toIconUrl ?? undefined,
      })
    }
    return map
  }, [dialogItems])

  const hasUnsavedChanges = useCallback((): boolean => {
    if (!isEnabled) return false
    const current = currentDepsRef.current
    const allDialog = dialogItemsRef.current
    const server = serverDataRef.current

    // Check for added items
    if (current.some(d => !server.some(s => s.uuid === d.uuid))) return true

    // Check for deleted items
    const allCurrentUuids = new Set([
      ...current.map(d => d.uuid),
      ...allDialog.map(d => d.uuid),
    ])
    if (server.some(s => !allCurrentUuids.has(s.uuid))) return true

    // Check for edited items (visible)
    if (
      current.some(d => {
        const s = server.find(s => s.uuid === d.uuid)
        if (!s) return false
        return (
          (d.color ?? null) !== (s.color ?? null) ||
          (d.description ?? null) !== (s.description ?? null)
        )
      })
    ) {
      return true
    }

    // Check for edited items (non-visible, via dialog)
    if (
      allDialog.some(d => {
        if (current.some(c => c.uuid === d.uuid)) return false
        const s = server.find(s => s.uuid === d.uuid)
        if (!s) return false
        return (
          (d.color ?? null) !== (s.color ?? null) ||
          (d.description ?? null) !== (s.description ?? null)
        )
      })
    ) {
      return true
    }

    return false
  }, [isEnabled])

  return {
    dependencies,
    onDependenciesChange,
    fetchDependencies,
    saveDependencies,
    removeByWbsItemUuids,
    setGridApi,
    getGridApi,
    isEnabled,
    dialogItems,
    updateLinkFromDialog,
    removeLinkFromDialog,
    wbsItemInfoMap,
    hasUnsavedChanges,
  }
}
